import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import type { BenchStageDeselection } from '@siliconx/shared';
import { qk } from '../../lib/queryKeys';
import {
  approveBenchPairing,
  denyBenchPairing,
  lookupBenchPairing,
  normalizeUserCodeInput,
} from '../../lib/bench';
import { TestSelectionPanel } from '../../components/bench/TestSelectionPanel';
import {
  buildDeselections,
  parseTestOptions,
  removedKey,
  summarizeDeselections,
} from '../../components/bench/testSelection';

/**
 * Bench pairing approval. The bench agent shows a QR code (this page with
 * ?code=XXXX-XXXX) and the same code as text. The technician signs in (the
 * root beforeLoad stashes this URL and restores it after Cognito) and taps
 * Approve once. Everything in the device card was reported by the device
 * and is unverified, so it is rendered as plain text and labelled as such.
 * The one-tap approval with device details and source IP is the defense
 * against a QR link for someone else's device (RFC 8628 section 5.4).
 */
export const Route = createFileRoute('/bench/pair')({
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === 'string' ? { code: search.code } : {},
  component: BenchPairRoute,
});

function BenchPairRoute() {
  const { code } = Route.useSearch();
  return <BenchPairView initialCode={code ?? null} />;
}

function errorStatus(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

function lookupErrorMessage(err: unknown): string {
  if (errorStatus(err) === 429) return 'Too many attempts. Wait 10 minutes, then try again.';
  return 'This code is not valid or has expired. Check the code on the bench screen.';
}

function errorCode(err: unknown): string | undefined {
  return (err as { body?: { error?: { code?: unknown } } } | null)?.body?.error?.code as string | undefined;
}

/**
 * Approve failures that mean the options on screen are out of date (or the caller's rights changed, `admin_required`): drop the
 * selection, close the confirm step and fetch the options again.
 */
const REVIEW_AGAIN_CODES = new Set(['selection_stale', 'selection_invalid', 'selection_disabled', 'admin_required']);

/**
 * Approve failures that mean the request itself was unreadable (client and server schemas out of step): resending the same list
 * cannot succeed, so drop the selection and close the confirm step. The options are not stale, so no refetch.
 */
const DROP_SELECTION_CODES = new Set(['invalid_body', 'invalid_selection', 'payload_too_large']);

/**
 * Fixed copy per error code. The server message is never shown: a refusal must not echo anything the user, or the device,
 * sent. Anything unrecognised falls back to the lookup copy.
 */
function approveErrorMessage(err: unknown): string {
  switch (errorCode(err)) {
    case 'admin_required': return 'Only an org admin can remove tests. Approving runs all tests.';
    case 'selection_disabled': return 'Choosing tests is not available right now. Approve to run every test.';
    case 'selection_stale': return 'Test profiles changed; review again.';
    case 'selection_invalid': return 'A selected test is not valid for this machine. Review the options again.';
    case 'too_many_selection_attempts': return 'Too many refused selections. Wait 10 minutes, then try again.';
    case 'not_authorized': return 'You are not allowed to approve this pairing.';
    case 'try_again': return 'The system is busy. Try again in a moment.';
    case 'invalid_body':
    case 'invalid_selection':
    case 'payload_too_large': return 'The selection could not be read, so it was cleared. Approving runs all tests.';
    default: break;
  }
  const status = errorStatus(err);
  if (status === 503) return 'The system is busy. Try again in a moment.';
  if (status !== undefined && status >= 500) return 'Something went wrong. Try again.';
  return lookupErrorMessage(err);
}

export function BenchPairView({ initialCode }: { initialCode: string | null }) {
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(initialCode ? normalizeUserCodeInput(initialCode) : null);
  const [input, setInput] = useState(initialCode ?? '');
  const [inputError, setInputError] = useState<string | null>(
    initialCode && !normalizeUserCodeInput(initialCode) ? 'Enter the 8 letters shown on the bench screen.' : null,
  );
  const [denied, setDenied] = useState(false);

  const lookup = useQuery({
    queryKey: qk.bench.pairing(code ?? ''),
    queryFn: () => lookupBenchPairing(code!),
    enabled: code !== null && !denied,
    retry: false,
    staleTime: Infinity,
  });

  // SIL-2000. Absent or unreadable testOptions: no panel, and Approve posts exactly what it always did.
  const options = useMemo(() => parseTestOptions(lookup.data?.testOptions), [lookup.data]);
  // `deviceIndex:stageIndex` of every optional test the admin unchecked.
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const deselections = useMemo(() => (options ? buildDeselections(options, removed) : []), [options, removed]);

  function setStage(deviceIndexes: number[], stageIndex: number, remove: boolean) {
    setRemoved((prev) => {
      const next = new Set(prev);
      for (const i of deviceIndexes) {
        if (remove) next.add(removedKey(i, stageIndex));
        else next.delete(removedKey(i, stageIndex));
      }
      return next;
    });
  }

  const approve = useMutation({
    mutationFn: (list: BenchStageDeselection[]) =>
      list.length > 0 ? approveBenchPairing(code!, list) : approveBenchPairing(code!),
    onSuccess: ({ runId }) => navigate({ to: '/bench/runs/$runId', params: { runId } }),
    onError: (err) => {
      const c = errorCode(err);
      if (c && REVIEW_AGAIN_CODES.has(c)) {
        setRemoved(new Set());
        setConfirming(false);
        void lookup.refetch();
      } else if (c && DROP_SELECTION_CODES.has(c)) {
        setRemoved(new Set());
        setConfirming(false);
      }
    },
  });

  const deny = useMutation({
    mutationFn: () => denyBenchPairing(code!),
    onSuccess: () => setDenied(true),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeUserCodeInput(input);
    if (!normalized) {
      setInputError('Enter the 8 letters shown on the bench screen.');
      return;
    }
    setInputError(null);
    setCode(normalized);
  }

  const showForm = code === null || lookup.isError;
  const busy = approve.isPending || deny.isPending;
  const summary = summarizeDeselections(deselections);
  const d = lookup.data;

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4">
      <PageHeader
        title="Pair a bench device"
        subtitle="Approve only the device that is on the bench in front of you."
      />

      {denied && (
        <InsightBanner tone="neutral" lead="Pairing denied.">
          The bench device cannot send results to your account. It shows a new code if someone pairs it again.
        </InsightBanner>
      )}

      {lookup.isError && !denied && (
        <InsightBanner tone="amber" lead={lookupErrorMessage(lookup.error)} />
      )}

      {showForm && !denied && (
        <Card>
          <form onSubmit={submit} className="space-y-3">
            <label htmlFor="bench-pair-code" className="block text-sm font-medium">
              Pairing code
            </label>
            <input
              id="bench-pair-code"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XXXX-XXXX"
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-lg tracking-widest"
            />
            {inputError && <p className="text-sm text-status-red">{inputError}</p>}
            <Button type="submit">Continue</Button>
          </form>
        </Card>
      )}

      {lookup.isPending && code !== null && !denied && <p className="text-sm text-muted-foreground">Looking up the device...</p>}

      {d && !denied && !lookup.isError && (
        <Card className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-lg tracking-widest">{code}</span>
            <StatusPill tone="amber" title="Every value below was sent by the device itself.">
              Reported by device, unverified
            </StatusPill>
          </div>

          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Chassis serial</dt>
            <dd className="font-mono break-all">{d.deviceSummary.chassisSerial ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Model</dt>
            <dd className="break-all">{d.deviceSummary.productName ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Vendor</dt>
            <dd className="break-all">{d.deviceSummary.sysVendor ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Board serial</dt>
            <dd className="font-mono break-all">{d.deviceSummary.boardSerial ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">MAC address</dt>
            <dd className="font-mono">{d.deviceSummary.primaryMac ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Devices found</dt>
            <dd>{d.deviceSummary.deviceCount ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Source IP</dt>
            <dd className="font-mono">{d.sourceIp}</dd>
          </dl>

          {d.siteMatch ? (
            <p className="flex items-center gap-2 text-sm text-status-green">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              This IP is inside a registered bench site of your organization.
            </p>
          ) : (
            <p className="flex items-center gap-2 text-sm text-status-amber">
              <ShieldAlert className="h-4 w-4" aria-hidden />
              This IP is not a registered bench site. Results will be marked self-reported.
            </p>
          )}

          {options && (
            <TestSelectionPanel
              options={options}
              removed={removed}
              onSet={setStage}
              disabled={busy || confirming || lookup.isFetching}
            />
          )}

          {(approve.isError || deny.isError) && (
            <p role="alert" className="text-sm text-status-red">
              {approve.isError ? approveErrorMessage(approve.error) : lookupErrorMessage(deny.error)}
            </p>
          )}

          {confirming && summary.tests > 0 && (
            <InsightBanner tone="amber" lead="Confirm partial scope">
              Partial scope: {summary.tests === 1 ? '1 optional test' : `${summary.tests} optional tests`} removed on{' '}
              {summary.devices === 1 ? '1 device' : `${summary.devices} devices`}. The run is recorded as partial scope, and those
              devices and the machine grade at most Normal wear, never Like new.
            </InsightBanner>
          )}

          <div className="flex gap-3">
            {confirming && summary.tests > 0 ? (
              <>
                <Button onClick={() => approve.mutate(deselections)} disabled={busy}>
                  Confirm and start
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
                  Back
                </Button>
              </>
            ) : (
              <Button
                onClick={() => (deselections.length > 0 ? setConfirming(true) : approve.mutate([]))}
                disabled={busy || lookup.isFetching}
              >
                Approve
              </Button>
            )}
            <Button variant="secondary" onClick={() => deny.mutate()} disabled={busy}>
              Deny
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
